import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Box, Container, Skeleton, Typography } from '@mui/material'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'
import { Mousewheel } from 'swiper/modules'
import 'swiper/css'
import FoodAreaCard from './FoodAreaCard'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useAppDispatch, useAppSelector } from '../../../../store'
import { selectLocation } from '../../../../store/authSlice'
import {
  FoodArea,
  getFoodAreasByLocation,
  selectFoodAreas,
  selectFoodAreasLoading,
} from '../../../../store/foodAreaSlice'
import { ROUTES } from '../../../../helpers/routes-helper'
import { okaoAxiosNoAuth } from '../../../../helpers/axios'
import { Restaurant } from '../../../../types'
import { DEFAULT_CARD_IMAGE } from '../../../../helpers/static-constants'
import ImgIcon from '../../../../components/ui/ImgIcon'
import theme, { SERIF_FONT, MONO_FONT } from '../../../../theme'

const SectionContainer = styled(Box)`
  width: 100%;
  padding: 40px 0;
  background: linear-gradient(
    180deg,
    ${theme.palette.background.paper} 0%,
    ${theme.palette.background.default} 100%
  );
`

const Eyebrow = styled(Typography)`
  && {
    font-family: ${MONO_FONT};
    font-size: 0.72rem;
    font-weight: 500;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${theme.palette.text.secondary};
    margin-bottom: 10px;
  }
`

const TopBar = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`

const SectionHeader = styled(Typography)`
  && {
    font-family: ${SERIF_FONT};
    font-size: clamp(1.6rem, 4vw, 1.9rem);
    font-weight: 500;
    color: ${theme.palette.text.primary};
    letter-spacing: -0.015em;
  }
`

const NavButtons = styled(Box)`
  display: flex;
  align-items: center;
  gap: 0;
`

const SwiperWrapper = styled(Box)`
  overflow: hidden;

  .swiper {
    overflow: visible;
  }

  .swiper-slide {
    height: auto !important;
  }
`

const SKELETON_COUNT = 3

interface FoodAreasSectionProps {
  loading?: boolean
}

const FoodAreasSection: React.FC<FoodAreasSectionProps> = ({
  loading = false,
}) => {
  const { navigate, theme, isSmallScreen } = useAppUtilities()
  const dispatch = useAppDispatch()
  const swiperRef = useRef<SwiperRef>(null)
  const location = useAppSelector(selectLocation)
  const apiFoodAreas = useAppSelector(selectFoodAreas)
  const apiLoading = useAppSelector(selectFoodAreasLoading)

  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const [previewImages, setPreviewImages] = useState<Record<string, string[]>>(
    {},
  )

  useEffect(() => {
    if (location?.latitude != null && location?.longitude != null) {
      void dispatch(
        getFoodAreasByLocation({
          lat: location.latitude,
          lng: location.longitude,
        }),
      )
    }
  }, [location?.latitude, location?.longitude, dispatch])

  useEffect(() => {
    if (apiFoodAreas.length === 0) return
    let cancelled = false

    const fetchPreviews = async (): Promise<void> => {
      const entries = await Promise.all(
        apiFoodAreas.map(async (area) => {
          try {
            const { data } = await okaoAxiosNoAuth().get<Restaurant[]>(
              `/food-areas/${area.foodAreaId}/restaurants`,
            )
            const images = (data ?? [])
              .map((r) => r.imageUrl)
              .filter(
                (url): url is string =>
                  Boolean(url) && url !== DEFAULT_CARD_IMAGE,
              )
              .slice(0, 3)
            return [area.foodAreaId, images] as [string, string[]]
          } catch {
            return [area.foodAreaId, []] as [string, string[]]
          }
        }),
      )
      if (!cancelled) {
        setPreviewImages(Object.fromEntries(entries))
      }
    }

    void fetchPreviews()
    return () => {
      cancelled = true
    }
  }, [apiFoodAreas])

  const foodAreas: FoodArea[] = apiFoodAreas
  const isLoading = loading || apiLoading

  const handlePrev = (): void => {
    swiperRef.current?.swiper.slidePrev()
  }

  const handleNext = (): void => {
    swiperRef.current?.swiper.slideNext()
  }

  const handleSlideChange = (swiper: { isBeginning: boolean; isEnd: boolean }): void => {
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }

  const handleAreaClick = (area: FoodArea): void => {
    navigate(
      ROUTES.FOOD_AREA_RESTAURANTS.replace(':foodAreaId', area.foodAreaId),
      { state: { areaName: area.name } },
    )
  }

  return (
    <SectionContainer>
      <Container component="div" maxWidth="lg">
        <Eyebrow variant="overline">Where the deciding starts</Eyebrow>
        <TopBar>
          <SectionHeader variant="h2">Where should you go?</SectionHeader>
          {!isLoading && foodAreas.length > 0 && !isSmallScreen && (
            <NavButtons>
              <ImgIcon
                iconElement={<IconChevronLeft stroke={2} />}
                alt="previous food area"
                imgSize={18}
                wrapperStyle={{
                  backgroundColor: theme.palette.secondary.contrastText,
                  height: '36px',
                  width: '36px',
                  outline: `2px solid ${theme.palette.grey[200]}`,
                  marginRight: '10px',
                  cursor: isBeginning ? 'not-allowed' : 'pointer',
                  opacity: isBeginning ? 0.5 : 1,
                }}
                onClick={isBeginning ? undefined : handlePrev}
              />
              <ImgIcon
                iconElement={<IconChevronRight stroke={2} />}
                alt="next food area"
                imgSize={18}
                wrapperStyle={{
                  backgroundColor: theme.palette.secondary.contrastText,
                  height: '36px',
                  width: '36px',
                  outline: `2px solid ${theme.palette.grey[200]}`,
                  cursor: isEnd ? 'not-allowed' : 'pointer',
                  opacity: isEnd ? 0.5 : 1,
                }}
                onClick={isEnd ? undefined : handleNext}
              />
            </NavButtons>
          )}
        </TopBar>

        <SwiperWrapper>
          {isLoading ? (
            <Box sx={{ display: 'flex', gap: '12px' }}>
              {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  sx={{
                    flexShrink: 0,
                    borderRadius: '16px',
                    width: '260px',
                    height: '190px',
                  }}
                />
              ))}
            </Box>
          ) : foodAreas.length === 0 ? (
            <Typography sx={{ color: 'text.secondary', fontSize: '14px', py: 2 }}>
              No neighborhoods mapped near you yet — check back soon.
            </Typography>
          ) : (
            <Swiper
              ref={swiperRef}
              mousewheel={{ forceToAxis: true }}
              modules={[Mousewheel]}
              grabCursor={true}
              onSlideChange={handleSlideChange}
              breakpoints={{
                0: { slidesPerView: 1.15, spaceBetween: 12 },
                600: { slidesPerView: 2, spaceBetween: 12 },
                960: { slidesPerView: 3, spaceBetween: 14 },
              }}
            >
              {foodAreas.map((area) => (
                <SwiperSlide key={area.foodAreaId}>
                  <FoodAreaCard
                    area={area}
                    previewImages={previewImages[area.foodAreaId]}
                    onClick={() => handleAreaClick(area)}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </SwiperWrapper>
      </Container>
    </SectionContainer>
  )
}

export default FoodAreasSection
